import {
    AccessibilityGradientCategoryColors,
    AccessibilityGradientNumericColors,
    AccessibilityNamedColors,
    GradientCategoryColors,
    GradientNumericColors,
    NamedColors,
    GradientOutageColors,
    GradientProjectColors,
} from '@theme/styles/theme-colors';
import {ThemeService} from '@core/utils/theme.service';

import {GeospatialColourType} from '../../../../../pages/geospatial-viewer/model/spatial-colours';

export class MapColor {
    value: string;
    color: string;
    type?: string;
    colorList: number[];
}

export class MapColoursService {
    /** Get a colour by name */
    public static getMapCategoryColour(value: string, theme: string): MapColor {
        const r: MapColor[] = MapColoursService.list(theme).filter((e) => e.value === value);
        if (r && r[0]) {
            return r[0];
        } else {
            return MapColoursService.getNoColour(theme);
        }
    }

    /** Get a colour by name, apply alpha, return hex string */
    public static getColourWithAlpha(value: string, theme: string, alpha: number = 1): string {
        const colourRgb = MapColoursService.getMapCategoryColour(value, theme).colorList;
        return `rgba(${colourRgb[0]}, ${colourRgb[1]}, ${colourRgb[2]}, ${alpha})`;
    }

    public static listByFilter(theme: string, valueRegex: string): MapColor[] {
        return MapColoursService.list(theme).filter((c) => c.value.includes(valueRegex));
    }

    /**
     * List of colours. If you want to access think directly, try getMapCategoryColour instead.
     * @param theme
     */
    public static list(theme: string): MapColor[] {
        const accessibility: boolean = ThemeService.isAccessibility(theme);
        const categoryColours = accessibility ? AccessibilityGradientCategoryColors : GradientCategoryColors;
        const numericColours = accessibility ? AccessibilityGradientNumericColors : GradientNumericColors;
        const namedColours = accessibility ? AccessibilityNamedColors : NamedColors;
        const outageColours = GradientOutageColors;
        const projectColours = GradientProjectColors;

        const result = [
            // NEW general colours: 1-5 tone colour scale (1 = good, 5 = bad)
            {
                value: GeospatialColourType.METRIC_COLOUR1,
                color: categoryColours.color1,
                colorList: MapColoursService.hexToRGB(categoryColours.color1),
            },
            {
                value: GeospatialColourType.METRIC_COLOUR2,
                color: categoryColours.color2,
                colorList: MapColoursService.hexToRGB(categoryColours.color2),
            },
            {
                value: GeospatialColourType.METRIC_COLOUR3,
                color: categoryColours.color3,
                colorList: MapColoursService.hexToRGB(categoryColours.color3),
            },
            {
                value: GeospatialColourType.METRIC_COLOUR4,
                color: categoryColours.color4,
                colorList: MapColoursService.hexToRGB(categoryColours.color4),
            },
            {
                value: GeospatialColourType.METRIC_COLOUR5,
                color: categoryColours.color5,
                colorList: MapColoursService.hexToRGB(categoryColours.color5),
            },
            // NEW alternate colours: 1-5 tone colour scale (1 = good, 5 = bad)
            {
                value: GeospatialColourType.METRIC_ALT_COLOUR1,
                color: numericColours.color1,
                colorList: MapColoursService.hexToRGB(numericColours.color1),
            },
            {
                value: GeospatialColourType.METRIC_ALT_COLOUR2,
                color: numericColours.color2,
                colorList: MapColoursService.hexToRGB(numericColours.color2),
            },
            {
                value: GeospatialColourType.METRIC_ALT_COLOUR3,
                color: numericColours.color3,
                colorList: MapColoursService.hexToRGB(numericColours.color3),
            },
            {
                value: GeospatialColourType.METRIC_ALT_COLOUR4,
                color: numericColours.color4,
                colorList: MapColoursService.hexToRGB(numericColours.color4),
            },
            {
                value: GeospatialColourType.METRIC_ALT_COLOUR5,
                color: numericColours.color5,
                colorList: MapColoursService.hexToRGB(numericColours.color5),
            },

            // Project colours
            {
                value: GeospatialColourType.PROJECT_SCOPE,
                color: projectColours.color1,
                colorList: MapColoursService.hexToRGB(projectColours.color1),
            },
            {
                value: GeospatialColourType.PROJECT_PROJECT,
                color: projectColours.color2,
                colorList: MapColoursService.hexToRGB(projectColours.color2),
            },
            {
                value: GeospatialColourType.PROJECT_BUILT,
                color: projectColours.color3,
                colorList: MapColoursService.hexToRGB(projectColours.color3),
            },
            {
                value: GeospatialColourType.PROJECT_GENERATED,
                color: projectColours.color4,
                colorList: MapColoursService.hexToRGB(projectColours.color4),
            },

            // Outage layer
            {
                value: GeospatialColourType.OUTAGE_COLOUR1,
                color: outageColours.color1,
                colorList: MapColoursService.hexToRGB(outageColours.color1),
            },
            {
                value: GeospatialColourType.OUTAGE_COLOUR2,
                color: outageColours.color2,
                colorList: MapColoursService.hexToRGB(outageColours.color2),
            },
            {
                value: GeospatialColourType.OUTAGE_COLOUR3,
                color: outageColours.color3,
                colorList: MapColoursService.hexToRGB(outageColours.color3),
            },
            {
                value: GeospatialColourType.OUTAGE_COLOUR4,
                color: outageColours.color4,
                colorList: MapColoursService.hexToRGB(outageColours.color4),
            },
            {
                value: GeospatialColourType.OUTAGE_COLOUR5,
                color: outageColours.color5,
                colorList: MapColoursService.hexToRGB(outageColours.color5),
            },

            // Static colours
            {
                value: GeospatialColourType.GREY,
                color: namedColours.grey,
                colorList: MapColoursService.hexToRGB(namedColours.grey),
            },
            {
                value: GeospatialColourType.BLUE,
                color: namedColours.blue,
                colorList: MapColoursService.hexToRGB(namedColours.grey),
            },
        ];
        return result;
    }

    public static getNoColour(theme: string): MapColor {
        const accessibility: boolean = ThemeService.isAccessibility(theme);
        const namedColours = accessibility ? AccessibilityNamedColors : NamedColors;
        return {
            value: 'NONE',
            color: namedColours.grey,
            colorList: MapColoursService.hexToRGB(namedColours.grey),
        };
    }

    static hexToRGB(hex: string): number[] {
        const r = parseInt(hex.slice(1, 3), 16),
            g = parseInt(hex.slice(3, 5), 16),
            b = parseInt(hex.slice(5, 7), 16);
        return [r, g, b];
    }
}
